import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ScrollText } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { PERMIT_STATUS, fmtDate } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ToneChip } from "../../components/common";
import { ActionButtons, useProjectPicker } from "../../components/ops";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const PERMIT_TYPES = {
  licencia_obra: "Licencia de obra", licencia_actividad: "Licencia de actividad",
  permiso_ocupacion_via: "Ocupación de vía pública", autorizacion_ambiental: "Autorización ambiental",
  licencia_primera_ocupacion: "Primera ocupación", declaracion_responsable: "Declaración responsable",
};

export default function Permisos() {
  const [pid, picker] = useProjectPicker();
  const users = useUsers();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const { data: items } = useQuery({
    queryKey: ["ops-permits", pid],
    queryFn: async () => (await api.get(`/proyectos/${pid}/ops/permisos`)).data,
  });
  const mut = useMutation({
    mutationFn: async () => (await api.post(`/proyectos/${pid}/ops/permisos`, f)).data,
    onSuccess: () => { toast.success("Permiso identificado"); setOpen(false); setF({}); qc.invalidateQueries({ queryKey: ["ops-permits", pid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  return (
    <div className="space-y-4" data-testid="permisos-page">
      <PageHeader title="Permisos, licencias y legalizaciones"
        subtitle="Sin justificante no hay presentación; sin resolución documental no hay aprobación. Los requerimientos generan fecha límite, tarea, documentos, alerta e impacto en planning."
        actions={<div className="flex items-center gap-2">{picker}
          <Button size="sm" onClick={() => setOpen(true)} data-testid="create-permit-open">Nuevo permiso</Button></div>}
        testid="permisos-title" />
      {!items?.length ? <EmptyState title="Sin permisos" description="Identifica el primer permiso o licencia del proyecto." /> : (
        <Table data-testid="permisos-table">
          <TableHeader><TableRow>
            <TableHead>Permiso</TableHead><TableHead>Tipo</TableHead><TableHead>Estado</TableHead><TableHead>Autoridad</TableHead>
            <TableHead>Justificante</TableHead><TableHead>Resolución</TableHead><TableHead>Requerimiento</TableHead>
            <TableHead>Responsable</TableHead><TableHead>Acciones</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((p) => (
              <TableRow key={p.id} data-testid={`permit-row-${p.id}`}>
                <TableCell className="max-w-56"><p className="font-medium text-slate-800">{p.title}</p>
                  <p className="text-xs text-slate-400">{p.id}</p></TableCell>
                <TableCell className="text-xs">{PERMIT_TYPES[p.permit_type] || p.permit_type}</TableCell>
                <TableCell><Chip map={PERMIT_STATUS} value={p.status} testid={`permit-status-${p.id}`} /></TableCell>
                <TableCell className="max-w-40 truncate text-xs text-slate-500">{p.authority || "—"}</TableCell>
                <TableCell className="max-w-40 truncate text-xs text-slate-500" title={p.justificante || ""}>{p.justificante || "—"}</TableCell>
                <TableCell className="text-xs" data-testid={`permit-resolution-${p.id}`}>
                  {p.resolution_outcome ? <ToneChip label={p.resolution_outcome} tone={p.resolution_outcome === "aprobada" ? "success" : "danger"} /> : "—"}
                </TableCell>
                <TableCell className="text-xs" data-testid={`permit-requirement-${p.id}`}>
                  {p.status === "requerimiento" || p.requirement_deadline ? (
                    <div>
                      {p.requirement_deadline && <p className="font-medium text-red-600">Límite: {fmtDate(p.requirement_deadline)}</p>}
                      {(p.requirement_documents || []).map((d) => <p key={d} className="text-slate-500">· {d}</p>)}
                      {p.planning_impact && <p className="text-amber-600">{p.planning_impact}</p>}
                    </div>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-xs">{userName(users.map, p.responsible_id)}</TableCell>
                <TableCell><ActionButtons item={p} endpoint={`/proyectos/${pid}/ops/permisos`} invalidate={[["ops-permits", pid]]}
                  extraFields={{ recibir_requerimiento: ["planning_impact"] }} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="create-permit-dialog">
          <DialogHeader><DialogTitle>Nuevo permiso / licencia</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Título *</Label><Input value={f.title || ""} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="create-permit-title" /></div>
            <div><Label>Tipo</Label>
              <Select value={f.permit_type || "licencia_obra"} onValueChange={(v) => setF({ ...f, permit_type: v })}>
                <SelectTrigger data-testid="create-permit-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PERMIT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Autoridad / organismo</Label><Input value={f.authority || ""} onChange={(e) => setF({ ...f, authority: e.target.value })} data-testid="create-permit-authority" /></div>
            <Button data-testid="create-permit-submit" disabled={!f.title || mut.isPending} onClick={() => mut.mutate()}>Identificar permiso</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
